import React, { useEffect, useState } from 'react'
import { motion, useAnimation } from 'framer-motion'
import styled from 'styled-components'
import media from 'css-in-js-media'
import Container from './Container'
import { BodyCopy, H6 } from './Typography'
import { Variables } from '../styles/Variables'
import { useInView } from 'react-intersection-observer'
import Img from 'gatsby-image'

import PlayPauseIcon from '../svg/pause-circle.svg'
import { DesktopView, FullAbsolute, MobileView } from '../styles/Utils'
import { TabsWrap, Tabs, Tab, TabContent, TabsContent } from './Tabs'

const TimelineWrapper = styled.div`
    position: relative;
    ${media('>=desktop')} {
    }

    .gatsby-image-wrapper {
        width: 100%;
        height: 100%;
    }
`

const TimelineBackground = styled.div`
    ${FullAbsolute}
    pointer-events: none;
    z-index: -1;
    :before {
        ${media('>=desktop')} {
            content: '';
            background: linear-gradient(90deg, rgba(0, 0, 0, 0.8) 0%, rgba(0, 0, 0, 0) 100%);
            position: absolute;
            right: 50%;
            left: 0;
            top: 0;
            bottom: 0;
            z-index: 1;
        }
    }
`

const InnerWrap = styled(motion.div)`
    position: relative;
    ${media('<desktop')} {
        padding-bottom: 40px;
    }

    .gatsby-image-wrapper {
        ${media('<desktop')} {
            margin-bottom: 40px;
        }
    }
`

const PlayPauseButton = styled.div`
    position: absolute;
    top: 20px;
    right: 20px;
    cursor: pointer;
    display: none;

    svg path {
        stroke: white;
    }
`

const Years = styled(motion.div)`
    position: absolute;
    top: 0;
    left: 108px;
    bottom: 0;
    display: flex;
    flex-direction: column;
    justify-content: space-evenly;
`

const Year = styled(motion.span)`
    font-family: ${Variables.font.title};
    font-size: 20px;
    line-height: 120%;
    text-align: center;
    letter-spacing: 0.0015em;
    color: white;
    position: relative;
    padding: 130px 0;
`

const YearLine = styled(motion.i)`
    position: absolute;
    top: 50%;
    right: 100%;
    width: 20px;
    height: 1px;
    background-color: white;
    margin-right: 36px;
    opacity: 0;
`

const LineWrap = styled(motion.div)`
    position: absolute;
    top: 0;
    left: 200px;
    bottom: 0;
    width: 2px;
    display: flex;
    flex-direction: column;
    justify-content: space-evenly;
`

const Line = styled(motion.div)`
    background-color: #9d9d9d;
    flex-grow: 10;
    overflow: hidden;
    position: relative;

    :first-child {
        flex-grow: 4;
    }
    :nth-child(3) {
        flex-grow: 6;
    }
`

const LineWhite = styled(motion.div)`
    background-color: #fff;
    position: absolute;
    top: 0;
    right: 0;
    left: 0;
    bottom: 0;
    opacity: 0;
    transform: translateY(-100%) translateZ(0px);
    will-change: transform;
`

const Dot = styled(motion.div)`
    width: 10px;
    height: 10px;
    border: 2px solid #9d9d9d;
    box-sizing: border-box;
    border-radius: 10px;
    margin-left: -4px;
    position: relative;
    z-index: 10;
    will-change: border-color, background-color;
`

const Milestones = styled(motion.div)`
    position: relative;
    left: 324px;
    color: white;
    padding: 80px 0;
`

const Milestone = styled(motion.div)`
    ${media('>=desktop')} {
        width: 293px;
        will-change: transform opacity;
        opacity: 0;
        transform: translateX(100px) translateZ(0px);
        margin-bottom: 40px;
    }
    ${BodyCopy} {
        margin-top: 15px;
        font-weight: 500;
        line-height: 160%;
        ${media('>=desktop')} {
            margin-top: 10px;
        }
    }
`

const Details = styled.div`
    font-size: 12px;
    font-weight: 500;
    line-height: 120%;
    letter-spacing: 0.0125em;
    position: relative;
    margin-bottom: 15px;

    ${media('>=desktop')} {
        margin-bottom: 10px;
    }

    span {
        color: #cccccc;
        margin-left: 10px;
    }

    :before {
        content: '';
        position: absolute;
        right: 100%;
        top: 5px;
        margin-right: 10px;
        background: white;
        border-radius: 5px;
        width: 5px;
        height: 5px;
    }
`

const Timeline_TabsContent = styled(TabsContent)`
    margin-top: 40px;

    ${Milestone} {
        margin-top: 40px;
    }
`

const Timeline = ({ data }) => {
    const [isAnimation, setAnimation] = useState(false)
    const [getActiveYear, setActiveYear] = useState('2020')
    const { ref, inView } = useInView({
        rootMargin: '-400px',
        triggerOnce: true,
    })

    const { years, backgroundImage } = data
    const controls = useAnimation()

    useEffect(() => {
        if (inView) {
            controls.start('active')
        } else {
            controls.set('inactive')
        }
    }, [inView])

    const MilestoneAnimation = {
        active: {
            opacity: 1,
            x: 0,
        },
        inactive: {
            opacity: 0,
            x: 100,
        },
    }

    const MilestonesAnimation = {
        active: {
            transition: {
                staggerChildren: 2,
            },
        },
        inactive: {},
    }

    const MilestoneTransition = { type: 'spring', duration: 2.5 }

    const LineAnimation = {
        active: {
            opacity: 1,
            y: 0,
        },
        inactive: {
            opacity: 0,
            y: '-100%',
        },
    }

    const LinesAnimation = {
        active: {
            transition: {
                staggerChildren: 1,
            },
        },
        inactive: {},
    }

    const LineTransition = { type: 'tween', duration: 1 }

    const DotAnimation = {
        active: {
            background: '#fff',
            borderColor: '#fff',
            scale: [1, 1.2, 1],
        },
        inactive: {
            background: 'transparent',
            borderColor: '#9d9d9d',
        },
    }
    const DotTransition = { type: 'spring', duration: 0.5 }

    const YearAnimation = {
        active: {
            opacity: 1,
        },
        inactive: {
            opacity: 0,
        },
    }

    return (
        <>
            {years && (
                <TimelineWrapper>
                    <DesktopView>
                        <Container>
                            <InnerWrap ref={ref}>
                                <Years animate={controls}>
                                    <Year>
                                        <YearLine
                                            variants={YearAnimation}
                                            transition={{ type: 'tween', duration: 2, delay: 0 }}
                                        />
                                        2020
                                    </Year>
                                    <Year>
                                        <YearLine
                                            variants={YearAnimation}
                                            transition={{ type: 'tween', duration: 2, delay: 6 }}
                                        />
                                        2021
                                    </Year>
                                    <Year>
                                        <YearLine
                                            variants={YearAnimation}
                                            transition={{ type: 'tween', duration: 2, delay: 8 }}
                                        />
                                        2022
                                    </Year>
                                </Years>
                                <LineWrap animate={controls} variants={LinesAnimation}>
                                    <Line>
                                        <LineWhite variants={LineAnimation} transition={LineTransition} />
                                    </Line>
                                    <Dot variants={DotAnimation} transition={DotTransition} />
                                    <Line>
                                        <LineWhite variants={LineAnimation} transition={LineTransition} />
                                    </Line>
                                    <Dot variants={DotAnimation} transition={DotTransition} />
                                    <Line>
                                        <LineWhite variants={LineAnimation} transition={LineTransition} />
                                    </Line>
                                    <Dot variants={DotAnimation} transition={DotTransition} />
                                    <Line>
                                        <LineWhite variants={LineAnimation} transition={LineTransition} />
                                    </Line>
                                    <Dot variants={DotAnimation} transition={DotTransition} />
                                    <Line>
                                        <LineWhite variants={LineAnimation} transition={LineTransition} />
                                    </Line>
                                    <Dot variants={DotAnimation} transition={DotTransition} />
                                    <Line>
                                        <LineWhite variants={LineAnimation} transition={LineTransition} />
                                    </Line>
                                </LineWrap>
                                <Milestones animate={controls} variants={MilestonesAnimation}>
                                    {years.map(
                                        (year, i) =>
                                            year.milestones &&
                                            year.milestones.map((milestone, ii) => (
                                                <Milestone
                                                    key={ii}
                                                    variants={MilestoneAnimation}
                                                    transition={MilestoneTransition}
                                                >
                                                    <Details>
                                                        MILESTONE <span>{milestone.date}</span>
                                                    </Details>
                                                    <H6>{milestone.title}</H6>
                                                    <BodyCopy>{milestone.copy.copy}</BodyCopy>
                                                </Milestone>
                                            ))
                                    )}
                                </Milestones>

                                <TimelineBackground>
                                    <Img fluid={backgroundImage.fluid} alt="" />
                                </TimelineBackground>
                                <PlayPauseButton
                                    onClick={() => {
                                        isAnimation ? setAnimation(false) : setAnimation(true)
                                    }}
                                >
                                    <PlayPauseIcon />
                                </PlayPauseButton>
                            </InnerWrap>
                        </Container>
                    </DesktopView>
                    <MobileView>
                        <InnerWrap>
                            <Img fluid={backgroundImage.fluid} alt="" />
                            <Container>
                                <TabsWrap>
                                    <Tabs>
                                        {years.map((year, i) => (
                                            <Tab
                                                key={i}
                                                active={getActiveYear == year.year ? true : undefined}
                                                onClick={() => setActiveYear(year.year)}
                                            >
                                                {year.year}
                                            </Tab>
                                        ))}
                                    </Tabs>
                                </TabsWrap>
                                <Timeline_TabsContent>
                                    {years.map((year, i) => (
                                        <TabContent
                                            key={i}
                                            active={getActiveYear == year.year ? true : undefined}
                                            onClick={() => setActiveYear(year.year)}
                                        >
                                            {year.milestones.map((milestone, ii) => (
                                                <Milestone key={ii}>
                                                    <Details>
                                                        MILESTONE <span>{milestone.date}</span>
                                                    </Details>
                                                    <H6>{milestone.title}</H6>
                                                    <BodyCopy>{milestone.copy.copy}</BodyCopy>
                                                </Milestone>
                                            ))}
                                        </TabContent>
                                    ))}
                                </Timeline_TabsContent>
                            </Container>
                        </InnerWrap>
                    </MobileView>
                </TimelineWrapper>
            )}
        </>
    )
}

export default Timeline
